/**
 * Contratos do financeiro — contas a pagar (Fase 4).
 * Baixas (pagamentos) e contas a receber entram nas próximas fases.
 */
import { z } from "zod";
import { versionSchema } from "./cadastros";

export const PAYABLE_STATUS_LABEL = {
  aberto: "Em aberto",
  quitado: "Quitado",
  cancelado: "Cancelado",
} as const;
export type PayableStatus = keyof typeof PAYABLE_STATUS_LABEL;

export const INSTALLMENT_STATUS_LABEL = {
  aberta: "Em aberto",
  paga: "Paga",
  cancelada: "Cancelada",
} as const;
export type InstallmentStatus = keyof typeof INSTALLMENT_STATUS_LABEL;

/** Filtro por situação da parcela (vencida/a vencer consideram a data de hoje da empresa). */
export const PAYABLE_FILTERS = [
  "abertas",
  "vencidas",
  "a_vencer",
  "pagas",
  "canceladas",
  "todas",
] as const;
export type PayableFilter = (typeof PAYABLE_FILTERS)[number];

export const payableQuerySchema = z.object({
  situacao: z.enum(PAYABLE_FILTERS).default("abertas"),
  supplierId: z.uuid().optional(),
  /** vencimento de/até */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const dueDateUpdateSchema = z.object({
  dueDate: z.iso.date("Informe o novo vencimento"),
  ...versionSchema.shape,
});
export type DueDateUpdateInput = z.input<typeof dueDateUpdateSchema>;

export type PayableInstallmentRow = {
  id: string;
  payableId: string;
  payableNumber: string;
  supplierId: string;
  supplierName: string;
  description: string;
  documentNumber: string | null;
  purchaseEntryId: string | null;
  purchaseEntryNumber: string | null;
  number: number;
  installmentCount: number;
  dueDate: string;
  amount: string;
  paidAmount: string;
  openAmount: string;
  status: InstallmentStatus;
  overdue: boolean;
  daysToDue: number;
  version: number;
};

export type PayableSummary = {
  openTotal: string;
  overdueTotal: string;
  next7DaysTotal: string;
  openCount: number;
  overdueCount: number;
};

export type PayableList = {
  items: PayableInstallmentRow[];
  total: number;
  limit: number;
  offset: number;
  /** soma do valor em aberto das parcelas do filtro */
  filteredOpenTotal: string;
  summary: PayableSummary;
};

export type Payable = {
  id: string;
  number: string;
  supplierId: string;
  supplierName: string;
  supplierDocument: string | null;
  purchaseEntryId: string | null;
  purchaseEntryNumber: string | null;
  description: string;
  documentNumber: string | null;
  issueDate: string;
  totalAmount: string;
  status: PayableStatus;
  cancelledAt: string | null;
  cancelReason: string | null;
  notes: string | null;
  createdAt: string;
  createdByEmail: string | null;
  version: number;
  installments: {
    id: string;
    number: number;
    dueDate: string;
    amount: string;
    paidAmount: string;
    status: InstallmentStatus;
    document: string | null;
    overdue: boolean;
    version: number;
  }[];
};
